import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { t } from '../../../utils/translations';

const ArticleSettings = ({ settings, onSettingsChange, onImageUpload }) => {
  const [showImageCropper, setShowImageCropper] = useState(false);

  const categoryOptions = [
    { value: 'news', label: 'News / أخبار / Haberler' },
    { value: 'events', label: 'Events / فعاليات / Etkinlikler' },
    { value: 'announcements', label: 'Announcements / إعلانات / Duyurular' },
    { value: 'academic', label: 'Academic / أكاديمي / Akademik' },
    { value: 'cultural', label: 'Cultural / ثقافي / Kültürel' },
    { value: 'sports', label: 'Sports / رياضة / Spor' }
  ];

  const authorOptions = [
    { value: 'admin', label: 'Admin User' },
    { value: 'editor1', label: 'Ahmed Al-Rashid' },
    { value: 'editor2', label: 'Fatima Hassan' },
    { value: 'editor3', label: 'Omar Khalil' },
    { value: 'editor4', label: 'Layla Mahmoud' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft / مسودة / Taslak' },
    { value: 'review', label: 'Under Review / قيد المراجعة / İnceleme Altında' },
    { value: 'scheduled', label: 'Scheduled / مجدول / Planlanmış' },
    { value: 'published', label: 'Published / منشور / Yayınlanmış' }
  ];

  const handleSettingChange = (field, value) => {
    onSettingsChange(field, value);
  };

  const handleImageUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleSettingChange('featuredImage', e?.target?.result);
        onImageUpload(file);
      };
      reader?.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    handleSettingChange('featuredImage', '');
  };

  return (
    <div className="space-y-6">
      {/* Publication Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground font-arabic">
          {t('articleSettings.publicationSettings')}
        </h3>

        {/* Category */}
        <Select
          label={t('articleSettings.category')}
          options={categoryOptions}
          value={settings?.category || ''}
          onChange={(value) => handleSettingChange('category', value)}
          placeholder={t('articleSettings.selectCategory')}
          required
        />

        {/* Author */}
        <Select
          label={t('articleSettings.author')}
          options={authorOptions}
          value={settings?.author || ''}
          onChange={(value) => handleSettingChange('author', value)}
          placeholder={t('articleSettings.selectAuthor')}
          required
        />

        {/* Status */}
        <Select
          label={t('articleSettings.status')}
          options={statusOptions}
          value={settings?.status || 'draft'}
          onChange={(value) => handleSettingChange('status', value)}
          placeholder={t('articleSettings.selectStatus')}
        />

        {/* Publication Date */}
        <Input
          label={t('articleSettings.publicationDate')}
          type="datetime-local"
          value={settings?.publishDate || ''}
          onChange={(e) => handleSettingChange('publishDate', e?.target?.value)}
          description={t('articleSettings.leaveEmptyToPublishImmediately')}
        />
      </div>
      {/* Featured Image */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground font-arabic">
          {t('articleSettings.featuredImage')}
        </h3>

        {settings?.featuredImage ? (
          <div className="space-y-3">
            <div className="relative group">
              <Image
                src={settings?.featuredImage}
                alt="Featured image preview showing article header visual"
                className="w-full h-48 object-cover rounded-lg border border-border"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowImageCropper(true)}
                  iconName="Crop"
                  iconPosition="left"
                  iconSize={16}
                >
                  {t('articleSettings.crop')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={removeImage}
                  iconName="Trash2"
                  iconPosition="left"
                  iconSize={16}
                >
                  {t('articleSettings.remove')}
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p>{t('articleSettings.imageDimensions')}</p>
              <p>{t('articleSettings.fileSize')}: 2.3 MB</p>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <Icon name="ImagePlus" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              {t('articleSettings.uploadFeaturedImage')}
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="featured-image-upload"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('featured-image-upload')?.click()}
              iconName="Upload"
              iconPosition="left"
              iconSize={16}
            >
              {t('articleSettings.chooseImage')}
            </Button>
          </div>
        )}
      </div>
      {/* Article Statistics */}
      {/* <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground font-arabic">
          {t('articleSettings.articleStatistics')}
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="Eye" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('articleSettings.views')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">
              {settings?.views || 0}
            </p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="MessageCircle" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('articleSettings.comments')}</span>
            </div>
            <p className="text-2xl font-bold text-foreground mt-1">
              {settings?.comments || 0}
            </p>
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          <p>{t('articleSettings.created')}: {new Date()?.toLocaleDateString()}</p>
          <p>{t('articleSettings.lastModified')}: {new Date()?.toLocaleString()}</p>
          <p>{t('articleSettings.autoSaved')}</p>
        </div>
      </div> */}
    </div>
  );
};

export default ArticleSettings;